/**
 * ClientContactModal
 *
 * Add/edit modal for a client's additional contacts (issue #458).
 *
 * Select-or-create: the caller picks between linking an existing system
 * user (someone who logs into the client portal) or a lightweight contact
 * that is only a name/phone/email — no account. This mirrors the DB shape:
 * `client_contacts.user_id` is nullable, and a lightweight contact instead
 * fills `contact_name` + `contact_phone` (+ optional `contact_email`).
 *
 * @module components/clients/ClientContactModal
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { listAssignableUsers } from '../../services/userService';

const CONTACT_ROLES = [
  { value: 'contact', label: 'Contacto' },
  { value: 'primary', label: 'Principal' },
  { value: 'secondary', label: 'Secundario' },
  { value: 'billing', label: 'Facturación' },
  { value: 'technical', label: 'Técnico' },
];

const EMPTY_FORM = {
  mode: 'existing', // 'existing' | 'lightweight'
  user_id: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  contact_role: 'contact',
  department: '',
  job_title: '',
  receives_notifications: true,
  receives_invoices: false,
  receives_reports: false,
};

/**
 * Build the payload the caller (ClientDetail) hands to
 * addClientContact/updateClientContact. `null`s out the fields that don't
 * apply to the chosen mode so a contact never ends up with a stale user_id
 * AND stale lightweight fields at the same time.
 */
export const buildContactPayload = (formData) => {
  const base = {
    contact_role: formData.contact_role || null,
    department: formData.department?.trim() || null,
    job_title: formData.job_title?.trim() || null,
    receives_notifications: Boolean(formData.receives_notifications),
    receives_invoices: Boolean(formData.receives_invoices),
    receives_reports: Boolean(formData.receives_reports),
  };

  if (formData.mode === 'existing') {
    return {
      user_id: formData.user_id || null,
      contact_name: null,
      contact_phone: null,
      contact_email: null,
      ...base,
    };
  }

  return {
    user_id: null,
    contact_name: formData.contact_name.trim(),
    contact_phone: formData.contact_phone.trim(),
    contact_email: formData.contact_email?.trim() || null,
    ...base,
  };
};

const ClientContactModal = ({ show, onHide, onSave, contact = null, loading = false }) => {
  const isEditing = Boolean(contact);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['assignable_users', 'client'],
    queryFn: () => listAssignableUsers('client'),
    enabled: show,
    staleTime: 1000 * 60 * 5,
  });
  const users = usersData?.data || [];

  useEffect(() => {
    if (!show) return;

    if (contact) {
      setFormData({
        mode: contact.user_id ? 'existing' : 'lightweight',
        user_id: contact.user_id || '',
        contact_name: contact.contact_name || '',
        contact_phone: contact.contact_phone || '',
        contact_email: contact.contact_email || '',
        contact_role: contact.contact_role || 'contact',
        department: contact.department || '',
        job_title: contact.job_title || '',
        receives_notifications: contact.receives_notifications !== false,
        receives_invoices: Boolean(contact.receives_invoices),
        receives_reports: Boolean(contact.receives_reports),
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [contact, show]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isValid =
    formData.mode === 'existing'
      ? Boolean(formData.user_id)
      : Boolean(formData.contact_name.trim() && formData.contact_phone.trim());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSave(buildContactPayload(formData));
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? 'Editar Contacto' : 'Agregar Contacto'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Tipo de contacto</Form.Label>
            <ToggleButtonGroup
              type="radio"
              name="contact-mode"
              value={formData.mode}
              onChange={(value) => handleChange('mode', value)}
              className="d-flex flex-wrap"
            >
              <ToggleButton
                id="contact-mode-existing"
                variant="outline-primary"
                value="existing"
                className="flex-fill"
                style={{ minHeight: 44 }}
              >
                <i className="bi bi-person-badge me-2"></i>
                Usuario existente
              </ToggleButton>
              <ToggleButton
                id="contact-mode-lightweight"
                variant="outline-primary"
                value="lightweight"
                className="flex-fill"
                style={{ minHeight: 44 }}
              >
                <i className="bi bi-person-lines-fill me-2"></i>
                Solo nombre y teléfono
              </ToggleButton>
            </ToggleButtonGroup>
            <Form.Text className="text-muted">
              {formData.mode === 'existing'
                ? 'La persona ya tiene una cuenta y puede entrar al portal del cliente.'
                : 'Para contactos que no necesitan acceder al sistema (ej. encargado de compras).'}
            </Form.Text>
          </Form.Group>

          {formData.mode === 'existing' ? (
            <Form.Group className="mb-3" controlId="contact-user-id">
              <Form.Label>Usuario *</Form.Label>
              {isLoadingUsers ? (
                <div>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando usuarios...
                </div>
              ) : (
                <Form.Select
                  value={formData.user_id}
                  onChange={(e) => handleChange('user_id', e.target.value)}
                  required
                >
                  <option value="">Seleccionar usuario...</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
                  {isEditing && formData.user_id && !users.some((u) => u.id === formData.user_id) && (
                    <option value={formData.user_id}>
                      {contact?.user?.full_name || 'Usuario actual'}
                    </option>
                  )}
                </Form.Select>
              )}
              {!isLoadingUsers && users.length === 0 && (
                <Form.Text className="text-muted">
                  No hay usuarios con rol "cliente" disponibles todavía.
                </Form.Text>
              )}
            </Form.Group>
          ) : (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="contact-name">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => handleChange('contact_name', e.target.value)}
                    placeholder="Ej. María Rodríguez"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="contact-phone">
                  <Form.Label>Teléfono *</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    placeholder="+506 ..."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3" controlId="contact-email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    placeholder="opcional"
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="contact-role">
                <Form.Label>Rol</Form.Label>
                <Form.Select
                  value={formData.contact_role}
                  onChange={(e) => handleChange('contact_role', e.target.value)}
                >
                  {CONTACT_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="contact-department">
                <Form.Label>Departamento</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  placeholder="Ej. Compras"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="contact-job-title">
                <Form.Label>Puesto</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                  placeholder="Ej. Jefe de Compras"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group>
            <Form.Label>Recibe</Form.Label>
            <div className="d-flex flex-column gap-2">
              <Form.Check
                type="checkbox"
                id="contact-receives-notifications"
                label="Notificaciones"
                checked={formData.receives_notifications}
                onChange={(e) => handleChange('receives_notifications', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="contact-receives-invoices"
                label="Facturas"
                checked={formData.receives_invoices}
                onChange={(e) => handleChange('receives_invoices', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="contact-receives-reports"
                label="Reportes"
                checked={formData.receives_reports}
                onChange={(e) => handleChange('receives_reports', e.target.checked)}
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading} style={{ minHeight: 44 }}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={loading || !isValid} style={{ minHeight: 44 }}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                Guardando...
              </>
            ) : (
              isEditing ? 'Actualizar' : 'Agregar Contacto'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ClientContactModal;
